import { computed, ref, watch, type Ref } from 'vue';
import type { TaskOverview } from '@composables/useTaskOverview';

/**
 * The tasks TasksTabPanel shows: the active ones (open and canceled), or the completed ones when
 * "Voltooid" is picked.
 *
 * - Only the active tasks come with the task overview. The completed ones are loaded the first time
 *   they are shown, and again after a failed load.
 * - A task completed in the task list changes in place, without a reload, so it moves from active to
 *   completed here at once (as when all tasks were loaded).
 */
export default function useTaskPanelTasks(taskOverview: () => TaskOverview, showCompleted: Readonly<Ref<boolean>>) {
    const completedHere = computed(() => taskOverview().active.data.value.filter((task) => task.isCompleted));
    const activeTasks = computed(() => taskOverview().active.data.value.filter((task) => !task.isCompleted));
    const completedTasks = computed(() => [
        ...completedHere.value,
        ...taskOverview().completed.data.value.filter((task) => !completedHere.value.some((here) => here.equals(task))),
    ]);
    const shownTasks = computed(() => (showCompleted.value ? completedTasks.value : activeTasks.value));

    const activeTasksCount = computed(() => activeTasks.value.length);
    const completedTasksCount = computed(() => taskOverview().completedCount.value + completedHere.value.length);
    /** Only the most recent completed tasks are loaded (COMPLETED_PAGE_SIZE) and there are more. */
    const completedListIsPartial = computed(() => taskOverview().completedLoaded.value
        && taskOverview().completedCount.value > taskOverview().completed.data.value.length);

    const completedState = ref<'idle' | 'loading' | 'failed'>('idle');
    watch(showCompleted, async (show) => {
        if (!show || taskOverview().completedLoaded.value || completedState.value === 'loading') {
            return;
        }

        completedState.value = 'loading';
        try {
            await taskOverview().loadCompleted();
            completedState.value = 'idle';
        } catch {
            completedState.value = 'failed';
        }
    }, { immediate: true });

    return { shownTasks, activeTasksCount, completedTasksCount, completedListIsPartial, completedState };
}
